import type {QueryClient} from '@tanstack/react-query'

import {isQueryPageOfItems} from '../../queries/query-data-helpers'
import {activeMemexItemsQueryFilter} from '../../queries/query-filters'
import {
  getPageTypeFromQueryKey,
  isPageTypeForGroupedItems,
  type MemexGroupedItemsQueryKey,
  type MemexSubIssuesQueryKey,
  type PageTypeForGroupedItems,
  type PaginatedMemexItemsQueryKey,
  paginatedMemexItemsQueryKey,
} from '../../queries/query-keys'
import {
  type MemexPageQueryData,
  pageParamForInitialPage,
  type PaginatedMemexItemsQueryVariables,
} from '../../queries/types'
import {getPaginatedQueryKeyWithVariables, getSubIssuesQueryKeyWithVariables} from '../helpers'

export function getActiveMemexItemsQueryVariables(queryClient: QueryClient): PaginatedMemexItemsQueryVariables {
  const keyWithVariables = getPaginatedQueryKeyWithVariables(queryClient)
  return keyWithVariables[2] as PaginatedMemexItemsQueryVariables
}

/**
 * Finds the query keys for all active queries that contain the item with the given id.
 * Returns undefined if no queries contain the item.
 * The same item may be present in multiple queries (e.g., if the item appears in multiple groups).
 * @param queryClient
 * @param itemId MemexItemModel id
 * @returns Array of query keys that contain the item, or undefined if none found
 */
export function getQueryKeysForItem(
  queryClient: QueryClient,
  itemId: number,
): Array<PaginatedMemexItemsQueryKey> | undefined {
  const queriesData = queryClient.getQueriesData<MemexPageQueryData>(activeMemexItemsQueryFilter)
  const matchingQueryKeys: Array<PaginatedMemexItemsQueryKey> = []
  for (const [queryKey, queryData] of queriesData) {
    if (queryData && 'nodes' in queryData && queryData.nodes.find(item => item.id === itemId)) {
      matchingQueryKeys.push(queryKey as PaginatedMemexItemsQueryKey)
    }
  }
  const subIssuesQueriesData = queryClient.getQueriesData<MemexPageQueryData>({
    queryKey: getSubIssuesQueryKeyWithVariables(queryClient),
  })
  for (const [queryKey, queryData] of subIssuesQueriesData) {
    if (queryData && 'nodes' in queryData && queryData.nodes.find(item => item.id === itemId)) {
      matchingQueryKeys.push(queryKey as MemexSubIssuesQueryKey)
    }
  }
  return matchingQueryKeys.length > 0 ? matchingQueryKeys : undefined
}

export function getQueryKeyForFirstGroupedItemsPage(queryClient: QueryClient, pageType: PageTypeForGroupedItems) {
  const keyWithVariables = getPaginatedQueryKeyWithVariables(queryClient)
  return [...keyWithVariables, pageType, pageParamForInitialPage] as MemexGroupedItemsQueryKey
}

/**
 * Checks the queryClient's query data to see if we have data for any query key with the `paginatedMemexItems` primaryKey.
 * For example, if we have data for a key like `['memexItems', {q: "is:draft"}]`,
 * even if the active query key is `['memexItems', {q: "is:pr"}]`.
 * This can be used to avoid passing `initialData` in `useMemexItemsPageParams`,
 * and ensure we fetch new data from the server instead.
 * @param queryClient
 * @returns Whether or not query data for any memex items related query key has been initialized
 */
export function hasMemexItemsQueryData(queryClient: QueryClient) {
  return queryClient.getQueriesData({queryKey: ['memex', paginatedMemexItemsQueryKey]}).length !== 0
}

/**
 * Looks up a MemexItemModel by its id and current group id. If no item is found, false is returned, otherwise true.
 * @param queryClient
 * @param itemId MemexItem id
 * @param groupId the group id where the MemexItemModel is believed to be in
 * @returns {boolean} whether or not we found an item with the id in this group
 */
export function isMemexItemInGroup(queryClient: QueryClient, itemId: number, groupId: string) {
  const queryKeysForItem = getQueryKeysForItem(queryClient, itemId)
  if (!queryKeysForItem) return false

  const pageTypes = queryKeysForItem.map(key => getPageTypeFromQueryKey(key))
  return pageTypes.find(pageType => isPageTypeForGroupedItems(pageType) && pageType.groupId === groupId) !== undefined
}

/**
 * As long as structuralSharing is disabled for the query, this method will
 * force a re-render by creating an array with a new identity, but re-using our
 * existing memex item models. This is needed because our MemexItemModels are
 * mutable, so when we change a column value, nothing else will force
 * a re-render.
 *
 * If we get to a point where MemexItemModels are immutable, we should be able
 * to do away with this function.
 * @param queryClient
 */
export function setMemexItemsToForceRerenderInQueryClient(queryClient: QueryClient) {
  queryClient.setQueriesData<MemexPageQueryData>(activeMemexItemsQueryFilter, queryData => {
    if (!isQueryPageOfItems(queryData)) {
      return queryData
    }

    return {...queryData}
  })
}
